// src/shared/scoringCore.js
//
// CANONICAL SCORING MATH — pure JavaScript, no React, no Firebase, no imports.
//
// This is the one place the product's derived numbers are defined: the
// performance axis, the wellbeing "worst dominates" axis, the composite ERI
// (v3), the quadrant / divergence / safety-flag diagnostic layer, the
// dashboard labels and the predictive heuristics.
//
// Two consumers, one source:
//   - the app: src/utils/scoring.js wraps computeCore() with the topic routing
//     used by the top/bottom-3 insights (calculateScores).
//   - Cloud Functions: functions/shared/scoringCore.js is a GENERATED CommonJS
//     copy of this file (npm run sync-scoring). The predeploy guard fails when
//     that copy is stale, so the dashboard and the warehouse can never score
//     the same person differently.
//
// Keep this file dependency-free and side-effect-free: the generator only
// rewrites `export const` / `export function` statements. Retune the model by
// editing SCORING_CONFIG; the logic below never hardcodes a threshold.
//
// v3 — DUAL-DIMENSION MODEL + COMPOSITE ERI
// Performance and Wellbeing are scored as two SEPARATE peer dimensions
// (perfScore, wellbeingScore) and the quadrant / divergence / safety flag are
// the diagnostic layer built from them. The headline ERI is a weighted
// composite of performance, wellbeing and cognitive — over whichever of the
// three are available, re-normalised (see SCORING_CONFIG.eri). Wellbeing
// inverts clinical symptom burden (higher = better). A DIVERGENCE signal
// fires when the two dimensions conflict (e.g. high drive + high symptom
// burden = masked burnout risk); that contradiction is treated as the most
// valuable signal, not noise.

// v4 (2026-09-23): a youth player's self-rated mental health (wellbeing_self)
// joins the wellbeing axis as a screener. Rows written before v4 carry v3 and
// never had that input; compare across versions with that in mind.
export const SCORING_VERSION = 4;

// =====================================================================
// SCORING CONFIG — the tunable knobs, all in one place
// =====================================================================
export const SCORING_CONFIG = {
    // Performance axis (X): metrics averaged, equal weight, into perfScore.
    performance: {
        metrics: ['focus', 'resilience', 'motivation', 'confidence', 'discipline',
                  'awareness', 'adaptability', 'sleep_perf', 'nutrition', 'injury_mgmt']
    },
    // ERI composite (v3): weights over the components that exist, re-normalised.
    // All three present -> 0.50 / 0.35 / 0.15. No cognitive -> 0.59 / 0.41.
    // No wellbeing (no screeners answered) -> performance (+ cognitive) only, and
    // the result carries wellbeingExcluded: true.
    eri: { perfWeight: 0.50, wellbeingWeight: 0.35, cogWeight: 0.15 },
    // Clinical sums above max * this factor are treated as contaminated (e.g. a
    // 0-100 slider written into a /21 instrument) and dropped from wellbeing.
    clinicalOverflowFactor: 1.5,
    // Weekly pulse workload slider (0-100, higher = heavier) -> stress label
    // when no GAD-7 sum is present.
    workload: { moderate: 40, high: 70 },
    // Quadrant thresholds: perf >= perfHigh => "high"; wellbeing >= wellbeingHealthy => "healthy".
    quadrant: { perfHigh: 60, wellbeingHealthy: 60 },
    // Wellbeing "worst-dominates" model (see formula block below).
    wellbeing: {
        tierPenalty: { 'none': 0, 'mild': 12, 'moderate': 42, 'moderately-severe': 58, 'severe': 72 },
        restWeight: 0.5   // co-occurring screeners contribute at half weight
    },
    // Divergence: flag when |perf - wellbeing| >= gap AND on opposite sides of thresholds.
    divergence: { gap: 25 }
};

// =====================================================================
// CLINICAL INSTRUMENT REGISTRY
// =====================================================================
// `latestStats` carries raw instrument SUMS keyed by metric id (higher =
// worse). Maxes below are derived from the actual question types in
// CLINICAL_MODULES (src/data/assessment-config.js) — NOT a flat
// questionCount * 3, because the modules mix response scales:
//
//   anxiety        GAD-7    7 items x 0-3  -> max 21 (validated cutoffs 5/10/15)
//   depression     PHQ-9    9 items x 0-3  -> max 27 (validated cutoffs 5/10/15/20)
//   sleep_clinical          5 items, mixed scales -> max 16
//   alcohol        AUDIT-C  3 select items x 0-4 -> max 12
//   substance      CAGE-AID 4 yes/no items -> max 4 (CAGE >= 2 lands in 'moderate')
//   eating                  6 items x 0-3  -> max 18
//   adhd           ASRS-6   6 items x 0-4  -> max 24
//   trauma       PC-PTSD-5  5 yes/no items -> max 5 (positive screen >= 3 -> 'moderate')
//   gambling       PGSI     9 items x 0-3  -> max 27 (proportional banding)
//
// NOTE ON OVERFLOW: the assessment's gate/screener questions share metric
// keys with the deep-dive modules (e.g. mh_anxiety_screen also sums into
// 'anxiety'), so a raw sum can slightly exceed the instrument max. Tier
// cutoffs are open-ended at the top, `percent` is clamped to 100, and sums
// beyond max * clinicalOverflowFactor are treated as contaminated (see below).
//
// TIER SCHEMES:
//   'gad7'         -> 0-4 none, 5-9 mild, 10-14 moderate, 15+ severe
//   'phq9'         -> 0-4 none, 5-9 mild, 10-14 moderate,
//                     15-19 moderately-severe, 20+ severe
//   'proportional' -> raw/max: <25% none, 25-<50% mild, 50-<75% moderate,
//                     >=75% severe (assumption for instruments without
//                     locally validated cutoffs).
export const CLINICAL_INSTRUMENTS = {
    'anxiety':        { label: 'Anxiety (GAD-7)',      max: 21, scheme: 'gad7' },
    'depression':     { label: 'Depression (PHQ-9)',   max: 27, scheme: 'phq9' },
    'sleep_clinical': { label: 'Sleep Disturbance',    max: 16, scheme: 'proportional' },
    'alcohol':        { label: 'Alcohol (AUDIT-C)',    max: 12, scheme: 'proportional' },
    'substance':      { label: 'Substance (CAGE-AID)', max: 4,  scheme: 'proportional' },
    'eating':         { label: 'Eating Habits',        max: 18, scheme: 'proportional' },
    'adhd':           { label: 'ADHD (ASRS-6)',        max: 24, scheme: 'proportional' },
    'trauma':         { label: 'Trauma (PC-PTSD-5)',   max: 5,  scheme: 'proportional' },
    'gambling':       { label: 'Gambling (PGSI)',      max: 27, scheme: 'proportional' }
};

// Youth single-question screeners (0-3): 0 none, 1 mild, 2 moderate, 3 severe.
// Each maps to the full instrument it stands in for; if BOTH are present the
// full instrument wins and the screener entry is dropped.
export const YOUTH_SCREENERS = {
    'anxiety_screener':    { label: 'Anxiety (Screener)',    fullMetric: 'anxiety' },
    'depression_screener': { label: 'Depression (Screener)', fullMetric: 'depression' },
    'sleep_screener':      { label: 'Sleep (Screener)',      fullMetric: 'sleep_clinical' },
    'eating_screener':     { label: 'Eating (Screener)',     fullMetric: 'eating' },
    'adhd_screener':       { label: 'ADHD (Screener)',       fullMetric: 'adhd' },
    'trauma_screener':     { label: 'Trauma (Screener)',     fullMetric: 'trauma' }
};

// Youth self-ratings: a 0-100 slider (higher = better) mapped onto the same
// 0-3 severity scale as the single-question screeners, so a player's own read
// of their mental health joins the worst-dominates wellbeing model. Bands:
// 75+ none, 50-74 mild, 25-49 moderate, under 25 severe. Under 25 therefore
// raises the safety flag like any severe screener - a deliberate choice.
export const SELF_RATINGS = {
    'wellbeing_self': { label: 'Mental health (self-rated)' }
};
export const tierSelfRating = (raw) => raw >= 75 ? 0 : raw >= 50 ? 1 : raw >= 25 ? 2 : 3;

// Context signals: a 0-100 answer (higher = better) that is RECORDED and passed
// through, and is deliberately not an input to anything else. It does not move
// the performance score, the wellbeing score, the ERI composite, the quadrant
// or the safety flag. `belonging` ("I feel like I belong on my team") is the
// first one asked on purpose as a context signal: a team-climate read for
// coaches (averaged server-side under the cohort floor, never per player) and,
// for the player, a pointer to their organization's own belonging page when the
// answer is low. Adding a key here changes no existing number, so it is not a
// SCORING_VERSION change.
// The parent check-in (ASSESSMENT_LIBRARY parent_v1): a parent's read on their
// player. Recorded for the warehouse under their own metric_ids and nothing
// else; not an input to any score here.
export const PARENT_METRICS = ['parent_sleep', 'parent_energy', 'parent_fuel', 'parent_enjoyment', 'parent_bounce',
    'parent_confidence', 'parent_belonging', 'parent_pain', 'parent_talks', 'parent_mood',
    'parent_car_ride', 'parent_balance', 'parent_words'];

export const CONTEXT_SIGNALS = {
    'physical_self': { label: 'Physical (self-rated)' },
    'belonging':     { label: 'Belonging', lowBelow: 50, coachNote: 'How much players feel they belong on this team.' }
};

// Severity rank used for "worst screener" comparisons.
export const TIER_RANK = { 'none': 0, 'mild': 1, 'moderate': 2, 'moderately-severe': 3, 'severe': 4 };

export const QUADRANTS = {
    thriving: {
        id: 'thriving',
        label: 'Thriving',
        description: 'Strong performance backed by solid wellbeing — sustainable output.'
    },
    building: {
        id: 'building',
        label: 'Building',
        description: 'Wellbeing is solid; performance has room to grow — capacity to build.'
    },
    running_hot: {
        id: 'running_hot',
        label: 'Running Hot',
        description: 'Driven but depleted — performance is outrunning wellbeing. Burnout risk.'
    },
    needs_support: {
        id: 'needs_support',
        label: 'Needs Support',
        description: 'Both performance and wellbeing are strained — prioritize recovery and support.'
    },
    unknown: {
        id: 'unknown',
        label: 'Unclassified',
        description: 'Not enough clinical data to place on the Performance x Wellbeing map.'
    }
};

// --- Tier helpers (pure) ---
export const tierGad7 = (raw) => raw >= 15 ? 'severe' : raw >= 10 ? 'moderate' : raw >= 5 ? 'mild' : 'none';
export const tierPhq9 = (raw) => raw >= 20 ? 'severe' : raw >= 15 ? 'moderately-severe' : raw >= 10 ? 'moderate' : raw >= 5 ? 'mild' : 'none';
export const tierProportional = (raw, max) => {
    const pct = max > 0 ? raw / max : 0;
    return pct >= 0.75 ? 'severe' : pct >= 0.5 ? 'moderate' : pct >= 0.25 ? 'mild' : 'none';
};
export const tierYouth = (raw) => raw >= 3 ? 'severe' : raw >= 2 ? 'moderate' : raw >= 1 ? 'mild' : 'none';

// Defensive numeric read: only finite numbers count, floored at 0.
export const safeNum = (val) => (typeof val === 'number' && isFinite(val)) ? Math.max(0, val) : null;

// The shape returned when there is nothing to score. Every field a consumer
// might read exists, so nothing downstream has to null-check the object.
export const emptyCore = () => ({
    hasData: false,
    perfScore: 0,
    perfCount: 0,
    eriScore: 0,
    eriComposition: {},
    wellbeingExcluded: true,
    cognitiveExcluded: true,
    cogScore: 0,
    contaminated: [],
    labels: { stress: "Unknown", sleep: "Unknown", focus: "Unknown" },
    predictions: { injury: "Calculating...", burnout: "Calculating...", readiness: "Calculating..." },
    clinical: {},
    wellbeingScore: null,
    wellbeingTier: 'Unknown',
    quadrant: QUADRANTS.unknown,
    divergence: { score: 0, flag: false, pattern: null },
    safetyFlag: false,
    context: {},
    scoringVersion: SCORING_VERSION
});

// =====================================================================
// WELLBEING FORMULA — "worst dominates"
// =====================================================================
// A naive average would let one SEVERE screen be masked by several mild
// ones. Instead:
//
//   wellbeingScore = clamp( 100
//                           - penalty(worst screener)
//                           - 0.5 * mean(penalties of remaining screeners),
//                           0, 100 )
//
// The single worst screener sets the floor; the rest contribute at half
// weight. Penalties are calibrated so ONE screener alone yields:
//   severe            -> 100 - 72 = 28  (deep in the at-risk band, < 60)
//   moderately-severe -> 100 - 58 = 42  (at-risk)
//   moderate          -> 100 - 42 = 58  (just below the 60 quadrant line)
//   mild              -> 100 - 12 = 88  (healthy, flagged as Mild)

// =====================================================================
// computeCore(stats) — stats is the flat metric map (latestStats)
// =====================================================================
export const computeCore = (stats) => {
    if (!stats || typeof stats !== 'object') return emptyCore();

    const TIER_PENALTY = SCORING_CONFIG.wellbeing.tierPenalty;
    const PERF_HIGH_THRESHOLD = SCORING_CONFIG.quadrant.perfHigh;
    const WELLBEING_HEALTHY_THRESHOLD = SCORING_CONFIG.quadrant.wellbeingHealthy;
    const DIVERGENCE_GAP = SCORING_CONFIG.divergence.gap;

    // --- 1. PERFORMANCE SCORE (equal-weight mean of the metrics present) ---
    let total = 0;
    let count = 0;
    SCORING_CONFIG.performance.metrics.forEach(key => {
        const val = stats[key];
        // 0 is a valid score; NaN/strings are not.
        if (typeof val === 'number' && isFinite(val)) {
            total += val;
            count++;
        }
    });
    const perfScore = count > 0 ? Math.round(total / count) : 0;

    // --- 2. COGNITIVE ---
    // When cognitive is unavailable (org toggle off / not taken) it is EXCLUDED
    // from the ERI composite and flagged; cogScore keeps a perfScore fallback
    // ONLY for the display heuristics (focus label, readiness) further down.
    const cognitiveExcluded = stats.cognitive_score === undefined || stats.cognitive_score === null;
    const cogScore = cognitiveExcluded ? perfScore : stats.cognitive_score;

    // --- 3. LABELS ---
    // Anxiety sum only counts on its own scale (see contamination guard below);
    // the weekly pulse's workload slider (0-100) is the fallback stress signal.
    const anxietyValid = typeof stats.anxiety === 'number' && isFinite(stats.anxiety) &&
        stats.anxiety <= CLINICAL_INSTRUMENTS.anxiety.max * SCORING_CONFIG.clinicalOverflowFactor;
    const anxietyRaw = anxietyValid ? stats.anxiety : 0;
    const workload = safeNum(stats.workload);
    let stressLabel = "Low";
    if (anxietyValid && anxietyRaw >= 10) stressLabel = "High";
    else if (anxietyValid && anxietyRaw >= 5) stressLabel = "Moderate";
    else if (!anxietyValid && workload !== null) {
        if (workload >= SCORING_CONFIG.workload.high) stressLabel = "High";
        else if (workload >= SCORING_CONFIG.workload.moderate) stressLabel = "Moderate";
    }
    // Fallback if neither is present but resilience is
    else if (!anxietyValid && workload === null && stats.resilience !== undefined) {
        if (stats.resilience < 40) stressLabel = "High";
        else if (stats.resilience < 70) stressLabel = "Moderate";
    }

    const sleepVal = stats.sleep_perf !== undefined ? stats.sleep_perf : 50;
    const sleepLabel = sleepVal < 50 ? "Poor" : (sleepVal < 75 ? "Moderate" : "Optimal");

    const focusVal = cogScore;
    const focusLabel = focusVal < 50 ? "Low" : (focusVal < 75 ? "Moderate" : "Optimal");

    // --- 4. PREDICTIVE HEURISTICS ---
    // A. Durability Status
    const injuryRiskVal = 100 - ((sleepVal + (stats.injury_mgmt || 50) + (stats.focus || 50)) / 3);
    let injuryLabel = "Optimal";
    if (injuryRiskVal > 60) injuryLabel = "Prioritize Recovery";
    else if (injuryRiskVal > 35) injuryLabel = "Stable";

    // B. Adaptation Reserve
    const anxietyNorm = anxietyValid ? (anxietyRaw / 21) * 100 : (workload !== null ? workload : 0);
    const burnoutVal = (anxietyNorm + (100 - sleepVal) + (100 - (stats.motivation || 50))) / 3;
    let burnoutLabel = "High Capacity";
    if (burnoutVal > 65) burnoutLabel = "Recharge Needed";
    else if (burnoutVal > 40) burnoutLabel = "Adapting";

    // C. Readiness
    const readinessVal = ((stats.resilience || 50) + sleepVal + cogScore) / 3;
    let readinessLabel = "Peak";
    if (readinessVal < 50) readinessLabel = "Active Recovery";
    else if (readinessVal < 75) readinessLabel = "Good";

    // --- 5. CLINICAL SCREENER TIERING (Wellbeing dimension, part 1) ---
    // One entry per screener actually present, each -> { raw, max, tier, percent }.
    const clinical = {};
    const contaminated = [];

    // 5a. Full instruments (raw sums, higher = worse)
    Object.keys(CLINICAL_INSTRUMENTS).forEach(key => {
        const raw = safeNum(stats[key]);
        if (raw === null) return; // absent or non-numeric -> skip, never NaN

        const inst = CLINICAL_INSTRUMENTS[key];
        // Contamination guard: gate questions can push a sum slightly past max, but
        // a value far beyond it is a different scale altogether (a 0-100 slider was
        // once written into 'anxiety' by the weekly pulse). Drop it rather than
        // tier it as severe.
        if (raw > inst.max * SCORING_CONFIG.clinicalOverflowFactor) { contaminated.push(key); return; }
        let tier;
        if (inst.scheme === 'gad7') tier = tierGad7(raw);
        else if (inst.scheme === 'phq9') tier = tierPhq9(raw);
        else tier = tierProportional(raw, inst.max);

        clinical[key] = {
            raw,
            max: inst.max,
            tier,
            // Clamped: gate questions share metric keys, so raw can exceed max.
            percent: Math.min(100, Math.round((raw / inst.max) * 100))
        };
    });

    // 5b. Youth single-question screeners (0-3). Only used when the full
    // instrument is absent — the full instrument's richer signal wins.
    Object.keys(YOUTH_SCREENERS).forEach(key => {
        const raw = safeNum(stats[key]);
        if (raw === null) return;
        if (clinical[YOUTH_SCREENERS[key].fullMetric]) return;

        clinical[key] = {
            raw,
            max: 3,
            tier: tierYouth(raw),
            percent: Math.min(100, Math.round((raw / 3) * 100))
        };
    });

    // 5c. Youth self-ratings (v4). The slider is stored as answered (0-100,
    // higher = better); here it becomes a 0-3 severity so it sits alongside
    // the other screeners. `selfRated` keeps the original for display.
    Object.keys(SELF_RATINGS).forEach(key => {
        const raw = safeNum(stats[key]);
        if (raw === null) return;
        const severity = tierSelfRating(Math.min(100, raw));
        clinical[key] = {
            raw: severity,
            max: 3,
            tier: tierYouth(severity),
            percent: Math.round((severity / 3) * 100),
            selfRated: Math.min(100, raw)
        };
    });

    // --- 6. WELLBEING SCORE (Wellbeing dimension, part 2) ---
    const clinicalKeys = Object.keys(clinical);
    let wellbeingScore = null;
    let wellbeingTier = 'Unknown';
    let worstTierRank = 0;

    if (clinicalKeys.length > 0) {
        const penalties = clinicalKeys
            .map(k => TIER_PENALTY[clinical[k].tier] || 0)
            .sort((a, b) => b - a); // descending: [0] is the worst screener

        const worstPenalty = penalties[0];
        const rest = penalties.slice(1);
        const restMean = rest.length > 0 ? rest.reduce((s, p) => s + p, 0) / rest.length : 0;

        wellbeingScore = Math.round(Math.min(100, Math.max(0, 100 - worstPenalty - SCORING_CONFIG.wellbeing.restWeight * restMean)));

        worstTierRank = clinicalKeys.reduce((r, k) => Math.max(r, TIER_RANK[clinical[k].tier] || 0), 0);

        // Overall tier: driven by the worst screener, escalated by the score
        // when co-occurring symptoms stack up.
        if (worstTierRank >= 3 || wellbeingScore < 45) wellbeingTier = 'At Risk';
        else if (worstTierRank === 2 || wellbeingScore < WELLBEING_HEALTHY_THRESHOLD) wellbeingTier = 'Elevated';
        else if (worstTierRank === 1 || wellbeingScore < 80) wellbeingTier = 'Mild';
        else wellbeingTier = 'Healthy';
    }
    // NOTE: no clinical data => wellbeingScore stays null and the quadrant is
    // 'unknown'. Absence of screening data is NOT evidence of health.

    // --- 7. QUADRANT (Performance x Wellbeing 2x2) ---
    let quadrant = QUADRANTS.unknown;
    if (wellbeingScore !== null) {
        const perfHigh = perfScore >= PERF_HIGH_THRESHOLD;
        const wellbeingHealthy = wellbeingScore >= WELLBEING_HEALTHY_THRESHOLD;
        if (perfHigh && wellbeingHealthy) quadrant = QUADRANTS.thriving;
        else if (!perfHigh && wellbeingHealthy) quadrant = QUADRANTS.building;
        else if (perfHigh && !wellbeingHealthy) quadrant = QUADRANTS.running_hot;
        else quadrant = QUADRANTS.needs_support;
    }

    // --- 8. DIVERGENCE DETECTION ---
    // Flag when the gap is meaningful (>= 25) AND the dimensions sit on
    // opposite sides of their thresholds — one says "fine", the other doesn't.
    let divergence = { score: 0, flag: false, pattern: null };
    if (wellbeingScore !== null) {
        const gap = Math.abs(perfScore - wellbeingScore);
        const perfHigh = perfScore >= PERF_HIGH_THRESHOLD;
        const wellbeingHealthy = wellbeingScore >= WELLBEING_HEALTHY_THRESHOLD;
        const conflicted = gap >= DIVERGENCE_GAP && (perfHigh !== wellbeingHealthy);

        let pattern = null;
        if (conflicted && perfHigh && !wellbeingHealthy) {
            pattern = 'High drive, low wellbeing — watch for masked burnout';
        } else if (conflicted && !perfHigh && wellbeingHealthy) {
            pattern = 'Strong wellbeing, low performance — capacity to build';
        }

        divergence = { score: gap, flag: conflicted, pattern };
    }

    // --- 9. SAFETY FLAG ---
    // True when depression is moderately-severe/severe OR any screener is
    // severe. (Item-level self-harm detection, e.g. PHQ-9 item 9, is out of
    // scope here — scoring only sees instrument sums.)
    const depressionTier = (clinical.depression || clinical.depression_screener || {}).tier || 'none';
    const anySevere = clinicalKeys.some(k => clinical[k].tier === 'severe');
    const safetyFlag = anySevere || depressionTier === 'moderately-severe' || depressionTier === 'severe';

    // --- 10. COMPOSITE ERI (v3) ---
    // Weighted over whichever components exist, re-normalised so the weights
    // always sum to 1. Wellbeing absent (no screeners) -> performance-led ERI
    // with wellbeingExcluded: true; cognitive absent -> cognitiveExcluded: true.
    const wellbeingExcluded = wellbeingScore === null;
    const parts = [{ key: 'performance', value: perfScore, weight: SCORING_CONFIG.eri.perfWeight }];
    if (!wellbeingExcluded) parts.push({ key: 'wellbeing', value: wellbeingScore, weight: SCORING_CONFIG.eri.wellbeingWeight });
    if (!cognitiveExcluded) parts.push({ key: 'cognitive', value: cogScore, weight: SCORING_CONFIG.eri.cogWeight });
    const weightSum = parts.reduce((s, p) => s + p.weight, 0);
    const eriScore = Math.round(parts.reduce((s, p) => s + p.value * (p.weight / weightSum), 0));
    const eriComposition = {};
    parts.forEach(p => { eriComposition[p.key] = Math.round((p.weight / weightSum) * 100) / 100; });

    // --- CONTEXT SIGNALS (recorded, never scored - see CONTEXT_SIGNALS) ---
    const context = {};
    Object.keys(CONTEXT_SIGNALS).forEach(key => {
        const v = safeNum(stats[key]);
        if (v !== null) context[key] = Math.min(100, v);
    });

    return {
        // "Has a baseline" means performance answers exist. A parent's check-in
        // (parent_* keys only) or an empty stats object is not a baseline, so the
        // weekly pulse, the dashboard lock and the coach context do not treat it
        // as one.
        hasData: count > 0,
        perfScore,
        perfCount: count,
        eriScore,
        eriComposition,
        wellbeingExcluded,
        cognitiveExcluded,
        cogScore,
        contaminated,
        labels: { stress: stressLabel, sleep: sleepLabel, focus: focusLabel },
        predictions: { injury: injuryLabel, burnout: burnoutLabel, readiness: readinessLabel },
        clinical,
        wellbeingScore,
        wellbeingTier,
        quadrant,
        divergence,
        safetyFlag,
        context,
        scoringVersion: SCORING_VERSION
    };
};
